import Card from '@/components/elements/Card/Card';
import { Button, Fab, LinkButton } from '@/components/elements/forms/buttons';
import Alert from '@/components/elements/notifications/Alert/Alert';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import Breadcrumbs from '@/components/modules/breadcrumbs/Breadcrumbs';
import Modal from '@/components/modules/modals/redesign/Modal/Modal';
import Seo from '@/components/modules/place/Seo';
import EmptyState from '@/features/ProfilePages/components/EmptyState';
import { getDetailsBreadcrumbs } from '@/helpers';
import {
  formatPhoneNumber,
  getBreadcrumbItems,
  getOpenHoursStrings,
  getUrlWithProtocol,
} from '@/helpers/placeHelperTS';
import useMobileView from '@/hooks/useMobileView';
import { __, _s } from '@/locale';
import { PLACE_IMPRESSION, placeService } from '@/services';
import { Place } from '@/types/state/shared';
import React, { useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Companies from '../../components/elements/Card/Companies';
import Address from '../../components/modules/place/Address';
import Contact from '../../components/modules/place/Contact';
import Links from '../../components/modules/place/Links';
import OpeningHours from '../../components/modules/place/OpeningHours';
import Photos from '../../components/modules/place/Photos';
import PhotoSlider from '../../components/modules/place/PhotoSlider';
import PlaceDetails from '../../components/modules/place/PlaceDetails';
import PlaceInfo from '../../components/modules/place/PlaceInfo';
import Services from '../../components/modules/place/Services';
import VerifyCompany from './components/VerifyCompany';
import { STARTING_POINT } from './constants/tracking';
import useReviews from './hooks/useReviews';
import NewCustomerModal from './modules/NewCustomerModal';
import VerifyCompanyModal from './modules/VerifyCompanyModal';
import { useProfilePage } from './ProfilePage.hooks';

const baseTranslationKey = 'features.ProfilePages';

type ProfilePageProps = {
  place: Place;
};

const ProfilePage = ({ place }: ProfilePageProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const location = useLocation();
  const tooltipTargetRef = useRef(null);
  const {
    render: ReviewsComponent,
    getTotalReviews,
    rating,
  } = useReviews({ placeId: place.id.toString(), tooltipTargetRef: tooltipTargetRef });
  const {
    lastVisitedPlaces,
    popularPlaces,
    similarPlaces,
    showPopup,
    handleCloseNewCustomerModal,
    showVerifyModal,
    handleVerifyCompanyClick,
    handleCloseVerifyModal,
  } = useProfilePage(place);
  const [showFullPhoneNumber, setShowFullPhoneNumber] = useState(false);
  const reviewRef = useRef(null);
  const serviesRef = useRef(null);
  const serviceCategory = place.about?.category?.main ?? place.about?.category?.sub;
  const companyPhoneNumber = place.contact?.phone ? formatPhoneNumber(place.contact.phone) : undefined;
  const isVerified = place.isVerifiedExternalPlace;
  const PLACE_YEAR = undefined; // TODO change to correct year when implemented in M3
  const companyEmail = place.contact?.email;
  const isPremium = place.about?.settings?.subscriptionType === 'profilepagepremium';
  const isBasic = false; // TODO change to correct type when implemented in M3

  const placeCategories = place.services;
  const [readMore, setReadMore] = useState<{ title: string; text: string }>(undefined);

  const missingServices = !placeCategories.some((service) => service.services.length > 0);

  const companySocials = {
    instagram: {
      name: place.contact?.instagram,
      link: place.contact?.instagram ? `https://www.instagram.com/${place.contact.instagram}` : '', // @TODO add sales trigger link to button when adding support for unverified places and basic subscriptions
    },
    facebook: {
      name: place.contact?.facebook,
      link: place.contact?.facebook ? `https://www.facebook.com/${place.contact.facebook}` : '', // @TODO add sales trigger link to button when adding support for unverified places and basic subscriptions
    },
    website: getUrlWithProtocol(place.contact?.website),
  };
  const openingHours = place.program ? getOpenHoursStrings(place.program) : undefined;

  const goBack = () => {
    if (location.state?.fromList) {
      history.goBack();
    } else {
      history.replace('/vad/var');
    }
  };

  const handleShowFullPhoneNumber = () => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_SHOW_PHONE_NUMBER_CLICK);
    setShowFullPhoneNumber(true);
  };

  const handlePhoneNumberClick = async () => {
    await placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_PHONE_NUMBER_CLICK);
    window.location.href = `tel:${companyPhoneNumber.fullNumber}`;
  };

  const handleOnReviewsClick = () => {
    if (reviewRef.current) {
      reviewRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleOnShowServicesClick = () => {
    if (serviesRef.current) {
      serviesRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleExternalWebsiteClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    placeService.storePlaceImpressions([place.id], PLACE_IMPRESSION.PLACE_EXTERNAL_BUSINESS_LINK_CLICK);
  };

  const breadcrumbs =
    place.about?.category?.main || place.about?.category?.sub
      ? getBreadcrumbItems(place)
      : getDetailsBreadcrumbs(place);
  const { about = {} } = place;
  const { images = [] } = about;
  const address = place.contact && place.contact.address ? place.contact.address : null;

  const HAS_MULTIPLE_IMAGES = images.length > 1;

  const handleClickReadMore = (title: string, text: string) => {
    setReadMore({ title, text });
  };

  const handleCloseReadMore = () => {
    setReadMore(undefined);
  };

  return (
    <PageViewLayout
      back
      hideBottomNavBar
      title={place.about.name}
      type={'mainView'}
      onBackButtonClick={goBack}
      wrapperClass="bg-surface_mp">
      <div className="text-fg_primary flex justify-center">
        <div
          className={`px-md pt-md pb-3xl flex w-full max-w-[1040px] flex-col ${isMobileView ? 'gap-2xl' : 'gap-4xl'}`}>
          <Breadcrumbs items={breadcrumbs} className="w-full text-sm" />
          <div className={`flex ${isMobileView ? 'gap-xl flex-col' : 'justify-between'}`}>
            <div className={`flex flex-col ${isMobileView ? 'gap-xs' : 'gap-md'}`}>
              <h1 className={`text-fg_primary ${isMobileView ? 'text-h-l' : 'text-h-xl'} font-bold`}>
                {place.about.name}
              </h1>
              <div className={`gap-xs flex ${isMobileView && isBasic ? 'flex-col' : 'items-center'}`}>
                {(address?.zipcode || address?.city) && (
                  <div className="text-fg_secondary text-m border-border_regular pr-2xs flex border-r">
                    {address?.street && isVerified && (!isMobileView || isBasic) && (
                      <span>{address.street.trim() + ','}&nbsp;</span>
                    )}
                    {address?.zipcode && <span>{address.zipcode.trim()}</span>}
                    {address?.city && <span>&nbsp;{address.city.trim()}</span>}
                  </div>
                )}

                <Rating
                  ratingCount={getTotalReviews ?? 0}
                  avgRating={rating}
                  size={'sm'}
                  showLink={!isMobileView || isBasic}
                  onReviewsClick={handleOnReviewsClick}
                />
              </div>
            </div>
            <div>
              {!isVerified && (
                <Button
                  size="lg"
                  block={isMobileView}
                  onClick={() => handleVerifyCompanyClick(STARTING_POINT.VERIFY_YOUR_COMPANY)}
                  iconNoFilter
                  rightIcon={<Icon variant="payment-method-bank-id" color="fg_primary_inverse" />}
                  label={__(`${baseTranslationKey}.cta`)}
                />
              )}

              {isVerified && !isBasic && !isPremium && !showFullPhoneNumber && companyPhoneNumber && (
                <Button size="lg" block={isMobileView} onClick={handleShowFullPhoneNumber} className="flex flex-col">
                  <span className="text-xl">{companyPhoneNumber.hiddenNumber}</span>
                  <span className="text-s">{_s(`${baseTranslationKey}.ctaVerified`)}</span>
                </Button>
              )}

              {isVerified && !isBasic && !isPremium && showFullPhoneNumber && companyPhoneNumber && (
                <LinkButton
                  size="lg"
                  block={isMobileView}
                  variant="primary"
                  onClick={handlePhoneNumberClick}
                  to={`tel:${companyPhoneNumber.fullNumber}`}
                  className="flex flex-col">
                  <span className="text-xl">{companyPhoneNumber.fullNumber}</span>
                  <span className="text-s">{_s(`${baseTranslationKey}.ctaVerified`)}</span>
                </LinkButton>
              )}

              {(isBasic || isPremium) && (
                <div className="gap-xl flex items-center">
                  {!isMobileView && companyPhoneNumber && (
                    <Fab
                      size="md"
                      variant="secondary"
                      className="shrink-0"
                      icon={<Icon variant="phone" color="fg_primary" />}
                      to={`tel:${companyPhoneNumber.fullNumber}`}></Fab>
                  )}

                  {(!isPremium || missingServices) && (
                    <LinkButton
                      size="lg"
                      variant="primary"
                      to={companySocials.website}
                      onClick={handleExternalWebsiteClick}
                      className={`${isMobileView ? '' : 'min-w-[240px]'}`}
                      block={isMobileView}
                      iconNoFilter
                      rightIcon={<Icon variant="external-link" color="fg_primary_inverse" />}>
                      <span className="flex flex-col">
                        <span className="text-xl">{_s(`${baseTranslationKey}.book.cta`)}</span>
                        <span className="text-s">{_s(`${baseTranslationKey}.book.description`)}</span>
                      </span>
                    </LinkButton>
                  )}

                  {isPremium && !missingServices && (
                    <Button
                      size="lg"
                      onClick={handleOnShowServicesClick}
                      block={isMobileView}
                      iconNoFilter
                      className="whitespace-nowrap"
                      label="Visa tjänster"></Button>
                  )}

                  {isMobileView && (
                    <div>
                      <Fab
                        size="md"
                        variant="secondary"
                        icon={<Icon variant="phone" color="fg_primary" />}
                        to={`tel:${companyPhoneNumber.fullNumber}`}></Fab>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {images.length >= 4 && <Photos photos={images} place={place} />}

          <div className={`flex ${isMobileView ? 'gap-2xl flex-col' : 'gap-md justify-between'}`}>
            <div className={`flex min-w-0 flex-1 flex-col ${isMobileView ? 'gap-2xl' : 'gap-4xl'}`}>
              {(isPremium || isBasic) && (
                <>
                  {isMobileView && !HAS_MULTIPLE_IMAGES && !isPremium && (
                    <Alert
                      variant="cation"
                      leftSlot={<Icon variant="warning" color="fg_notice_bold" />}
                      body={_s(`${baseTranslationKey}.book.alert`)}
                    />
                  )}
                  {images.length < 4 && images.length > 0 && <Photos photos={images} place={place} />}
                  {(!isMobileView || (HAS_MULTIPLE_IMAGES && isMobileView)) && !isPremium && (
                    <Alert
                      variant="cation"
                      leftSlot={<Icon variant="warning" color="fg_notice_bold" />}
                      body={_s(`${baseTranslationKey}.book.alert`)}
                    />
                  )}
                </>
              )}

              {isPremium && !missingServices && (
                <div className="gap-md flex flex-col">
                  <div className="flex justify-between">
                    <h2 className="text-h-m">{_s(`${baseTranslationKey}.book.cta`)}</h2>
                  </div>

                  <Alert
                    variant="cation"
                    leftSlot={<Icon variant="warning" color="fg_notice_bold" />}
                    body={_s(`${baseTranslationKey}.book.alert`)}
                  />

                  <div ref={serviesRef} className="scroll-mt-16">
                    {missingServices && (
                      <Card size={isMobileView ? 'lg' : 'xl'}>
                        <div className="gap-2xl flex flex-col">
                          <div className="flex items-center justify-between">
                            <h2 className="text-h-s">{_s(`${baseTranslationKey}.servicesCard.title`)}</h2>
                            {!isMobileView && (
                              <Button
                                size="md"
                                label={_s(`${baseTranslationKey}.servicesCard.cta`)}
                                iconNoFilter
                                rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                              />
                            )}
                          </div>
                          <EmptyState
                            body={_s(`${baseTranslationKey}.servicesCard.emptyState.body`)}
                            title={_s(`${baseTranslationKey}.servicesCard.emptyState.title`)}
                            icon={<img src="/images/illustrations/empty.svg" alt="" />}
                          />
                          {isMobileView && (
                            <Button
                              size="md"
                              label={_s(`${baseTranslationKey}.cta`)}
                              iconNoFilter
                              block
                              rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                            />
                          )}
                        </div>
                      </Card>
                    )}
                    {!missingServices &&
                      placeCategories
                        .filter((category) => category.services.length > 0)
                        .map((category, index) => (
                          <Services
                            services={category.services}
                            categoryName={category.name}
                            handleClickReadMore={handleClickReadMore}
                            key={index}
                            place={place}
                            _expanded={true}
                          />
                        ))}
                  </div>
                </div>
              )}

              {(isPremium || isBasic) && missingServices && (
                <PlaceInfo place={place} category={serviceCategory} isVerified={isVerified} year={PLACE_YEAR} />
              )}

              {isMobileView && isPremium && (
                <div>
                  <div className="relative" ref={tooltipTargetRef}></div>
                  <div ref={reviewRef} className="scroll-mt-16">
                    {ReviewsComponent}
                  </div>
                </div>
              )}

              {isVerified && isMobileView && (
                <>
                  <PlaceDetails
                    onAddWebSiteClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_WEBSITE)}
                    place={place}
                    isVerified={isVerified}
                    isBasic={isBasic}
                    isPremium={isPremium}
                  />
                  {isBasic && (
                    <div>
                      <div className="relative" ref={tooltipTargetRef}></div>
                      <div className={`flex flex-col ${isMobileView ? 'gap-2xl' : 'gap-4xl'}`}>
                        <div ref={reviewRef} className="scroll-mt-16">
                          {ReviewsComponent}
                        </div>
                        <Card size={isMobileView ? 'lg' : 'xl'}>
                          <div className="gap-2xl flex flex-col">
                            <div className="flex items-center justify-between">
                              <h2 className="text-h-s">{_s(`${baseTranslationKey}.servicesCard.title`)}</h2>
                              {!isMobileView && (
                                <Button
                                  size="md"
                                  label={_s(`${baseTranslationKey}.servicesCard.cta`)}
                                  iconNoFilter
                                  rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                                />
                              )}
                            </div>
                            <EmptyState
                              body={_s(`${baseTranslationKey}.servicesCard.emptyState.body`)}
                              title={_s(`${baseTranslationKey}.servicesCard.emptyState.title`)}
                              icon={<img src="/images/illustrations/empty.svg" alt="" />}
                            />
                            {isMobileView && (
                              <Button
                                size="md"
                                label={_s(`${baseTranslationKey}.servicesCard.cta`)}
                                iconNoFilter
                                block
                                rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                              />
                            )}
                          </div>
                        </Card>
                      </div>
                    </div>
                  )}
                  {(companyEmail || companyPhoneNumber) && (
                    <Contact
                      placeId={place.id}
                      email={companyEmail}
                      isVerified={isVerified}
                      onShowFullPhoneNumber={handleShowFullPhoneNumber}
                      onPhoneNumberClick={handlePhoneNumberClick}
                      onAddEmailClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_EMAIL)}
                      onAddPhoneClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_PHONE)}
                      phone={
                        companyPhoneNumber
                          ? showFullPhoneNumber
                            ? companyPhoneNumber.fullNumber
                            : companyPhoneNumber.hiddenNumber
                          : undefined
                      }
                      fullNumber={showFullPhoneNumber}
                    />
                  )}
                  {isVerified && (isPremium || isBasic) && (
                    <Links placeId={place.id} onWebsiteClick={handleExternalWebsiteClick} {...companySocials} />
                  )}
                  <Address
                    onAddAddressClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_ADDRESS)}
                    place={place}
                  />
                  {((isVerified && !(isPremium || isBasic)) || (openingHours && (isPremium || isBasic))) && (
                    <OpeningHours
                      openingHours={isBasic || isPremium ? openingHours : undefined}
                      showCta={!isVerified}
                    />
                  )}
                </>
              )}

              {!isPremium && !isBasic && (
                <PlaceInfo place={place} category={serviceCategory} isVerified={isVerified} year={PLACE_YEAR} />
              )}

              {(!isMobileView || (!isBasic && !isPremium)) && (
                <div>
                  <div className="relative" ref={tooltipTargetRef}></div>
                  <div ref={reviewRef} className="scroll-mt-16">
                    {ReviewsComponent}
                  </div>
                </div>
              )}

              {!isVerified && (
                <VerifyCompany
                  onVerifyCompanyClick={() => handleVerifyCompanyClick(STARTING_POINT.VERIFY_YOUR_COMPANY)}
                />
              )}
              {(!isMobileView || !isBasic) && !isPremium && (
                <Card size={isMobileView ? 'lg' : 'xl'}>
                  <div className="gap-2xl flex flex-col">
                    <div className="flex items-center justify-between">
                      <h2 className="text-h-s">{_s(`${baseTranslationKey}.servicesCard.title`)}</h2>
                      {!isMobileView && !isVerified && (
                        <Button
                          size="md"
                          label={_s(`${baseTranslationKey}.servicesCard.cta`)}
                          iconNoFilter
                          onClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_SERVICE)}
                          rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                        />
                      )}
                    </div>
                    <EmptyState
                      body={_s(`${baseTranslationKey}.servicesCard.emptyState.body`)}
                      title={_s(`${baseTranslationKey}.servicesCard.emptyState.title`)}
                      icon={<img src="/images/illustrations/empty.svg" alt="" />}
                    />
                    {isMobileView && !isVerified && (
                      <Button
                        size="md"
                        label={_s(`${baseTranslationKey}.servicesCard.cta`)}
                        iconNoFilter
                        block
                        onClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_SERVICE)}
                        rightIcon={<Icon variant="plus" color="fg_primary_inverse" />}
                      />
                    )}
                  </div>
                </Card>
              )}

              {(isPremium || isBasic) && !missingServices && (
                <PlaceInfo place={place} category={serviceCategory} isVerified={isVerified} year={PLACE_YEAR} />
              )}

              {(isPremium || isBasic) && images?.length > 0 && <PhotoSlider images={images} />}

              {!isMobileView && !isBasic && !isPremium && (
                <>
                  {Boolean(similarPlaces.length) && (
                    <Companies
                      companies={similarPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.similarCompanyCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                      city={address.city}
                    />
                  )}
                  {Boolean(popularPlaces.length) && (
                    <Companies
                      companies={popularPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.popularCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                      city={address.city}
                    />
                  )}
                  {lastVisitedPlaces.length > 0 && (
                    <Companies
                      companies={lastVisitedPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.recentlyViewedCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                    />
                  )}
                </>
              )}
            </div>

            <div className={`flex flex-col ${isMobileView ? 'gap-2xl' : 'gap-md max-w-[336px]'}`}>
              {(!isVerified || !isMobileView) && (
                <>
                  <PlaceDetails
                    onAddWebSiteClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_WEBSITE)}
                    place={place}
                    isVerified={isVerified}
                    isBasic={isBasic}
                    isPremium={isPremium}
                  />
                  {(companyEmail || companyPhoneNumber) && (
                    <Contact
                      placeId={place.id}
                      onAddEmailClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_EMAIL)}
                      onAddPhoneClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_PHONE)}
                      email={companyEmail}
                      isVerified={isVerified}
                      onShowFullPhoneNumber={handleShowFullPhoneNumber}
                      onPhoneNumberClick={handlePhoneNumberClick}
                      phone={
                        companyPhoneNumber
                          ? showFullPhoneNumber
                            ? companyPhoneNumber.fullNumber
                            : companyPhoneNumber.hiddenNumber
                          : undefined
                      }
                      fullNumber={showFullPhoneNumber}
                    />
                  )}
                  {isVerified &&
                    (isPremium || isBasic) &&
                    (companySocials.facebook || companySocials.instagram || companySocials.website) && (
                      <Links placeId={place.id} onWebsiteClick={handleExternalWebsiteClick} {...companySocials} />
                    )}
                  <Address
                    place={place}
                    onAddAddressClick={() => handleVerifyCompanyClick(STARTING_POINT.ADD_ADDRESS)}
                  />
                  {((isVerified && !(isPremium || isBasic)) || (openingHours && (isPremium || isBasic))) && (
                    <OpeningHours
                      openingHours={isBasic || isPremium ? openingHours : undefined}
                      showCta={!isVerified}
                    />
                  )}
                </>
              )}

              {isMobileView && !isBasic && !isPremium && (
                <>
                  {Boolean(similarPlaces.length) && (
                    <Companies
                      companies={similarPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.similarCompanyCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                      city={address.city}
                    />
                  )}
                  {Boolean(popularPlaces.length) && (
                    <Companies
                      companies={popularPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.popularCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                      city={address.city}
                    />
                  )}
                  {lastVisitedPlaces.length > 0 && (
                    <Companies
                      companies={lastVisitedPlaces}
                      title={{
                        bold: _s(`${baseTranslationKey}.recentlyViewedCard`),
                        normal: `${_s(`${baseTranslationKey}.company`)}`,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
      <Modal className={`${isMobileView ? 'h-screen' : 'md:max-w-[560px]'}`} isOpen={Boolean(readMore?.text)}>
        <div className={`relative flex flex-col ${isMobileView ? 'h-full w-full' : ''}`}>
          <Modal.Header
            newDesign={true}
            title={readMore?.title}
            floating={!isMobileView}
            onClose={handleCloseReadMore}
          />
          <div className={`p-xl gap-lg bg-surface_l4 flex h-full flex-col ${isMobileView ? '' : 'rounded-b-lg'}`}>
            <span className="whitespace-pre-wrap">{readMore?.text}</span>
          </div>
        </div>
      </Modal>
      {Seo({ place: place, isSistaminuten: false })}
      {showVerifyModal.show && (
        <VerifyCompanyModal place={place} trigger={showVerifyModal.trigger} onClose={handleCloseVerifyModal} />
      )}
      {showPopup && (
        <NewCustomerModal
          onClose={handleCloseNewCustomerModal}
          onVerify={() => handleVerifyCompanyClick(STARTING_POINT.NEW_CUSTOMER_MODAL)}
        />
      )}
    </PageViewLayout>
  );
};

export default ProfilePage;
